import { Chip, ChipTone } from "@/components/badge/chip";
import { cn } from "@/lib/utils";

// Channel-package lifecycle. Each channel build of the same version code lives
// or dies on its own store (doc may get rejected while cat stays online).
// State moves along the channel flow (per-app configurable, Settings →
// Channel flow); release logs only record rollout progress.
//
// The authoritative state catalog lives in the DB (`channel_state` table,
// GET /channel-states; created from the Channel flow editor, admin-gated).
// The hardcoded entries below are the seed/fallback so badges render before
// the catalog loads; `useChannelStates()` (hooks/channel-states.ts) syncs
// fetched rows in.
export interface ChannelStateDef {
  key: string;
  label: string;
  color?: string | null;
  nexts: string[];
  sort?: number;
}

const BUILTIN_DEFS: ChannelStateDef[] = [
  { key: "inreview", label: "审核中", color: "muted", nexts: ["rolling", "online", "rejected", "offline"] },
  { key: "rolling", label: "放量中", color: "yellow", nexts: ["online", "rejected", "offline"] },
  { key: "online", label: "线上", color: "emerald", nexts: ["rolling", "rejected", "offline"] },
  { key: "rejected", label: "被拒", color: "red", nexts: ["inreview", "rolling", "online", "archived"] },
  { key: "offline", label: "下线", color: "rose", nexts: ["inreview", "rolling", "online", "archived"] },
  { key: "archived", label: "已归档", color: "muted", nexts: [] },
];

// DB color tone key → Chip tone ('muted'/unknown → neutral).
const CHIP_TONES = new Set([
  "green", "emerald", "red", "yellow", "orange", "blue",
  "purple", "lime", "cyan", "teal", "rose",
]);
const toneOf = (color?: string | null): ChipTone =>
  color && CHIP_TONES.has(color) ? (color as ChipTone) : "neutral";

// Display registry (label/tone per key). Seeded with the built-ins and
// overlaid by DB rows; stale entries are kept so historical badges render.
export const ChannelStates: Record<string, { label: string; tone: ChipTone }> =
  Object.fromEntries(
    BUILTIN_DEFS.map((d) => [d.key, { label: d.label, tone: toneOf(d.color) }])
  );

// Keys/nexts offered in config UIs: the DB catalog once loaded.
let catalogKeys = BUILTIN_DEFS.map((d) => d.key);
let catalogNexts: Record<string, string[]> = Object.fromEntries(
  BUILTIN_DEFS.map((d) => [d.key, d.nexts])
);

/** Merge fetched catalog rows in (called by useChannelStates). */
export const registerChannelStates = (rows: ChannelStateDef[]) => {
  if (!rows.length) return;
  for (const r of rows) {
    ChannelStates[r.key] = { label: r.label, tone: toneOf(r.color) };
  }
  catalogKeys = rows.map((r) => r.key);
  catalogNexts = Object.fromEntries(rows.map((r) => [r.key, r.nexts ?? []]));
};

/** Catalog keys in flow order (DB sort). */
export const allChannelStateKeys = (): string[] => [...catalogKeys];

/** Default flow graph: every catalog state with its default nexts, plus the
 * reserved `start` entry (审核中). */
export const defaultChannelFlow = (): Record<string, string[]> => ({
  start: ["inreview"],
  ...Object.fromEntries(catalogKeys.map((k) => [k, catalogNexts[k] ?? []])),
});

// Default channel-release gate: version states whose versions accept channel
// releases (server DEFAULT_CHANNEL_RELEASABLE_STATES; per-app override in
// Settings → Channel flow, app.channelReleasableStates).
export const DEFAULT_CHANNEL_RELEASABLE_STATES = [180, 190, 200, 250];

/** Flow entry state (reserved `start` key; legacy configs default 审核中). */
export const channelFlowStart = (flow: Record<string, string[]>) =>
  flow.start?.[0] ?? "inreview";

export const ChannelStateChip = ({
  state,
  className,
}: {
  state?: string;
  className?: string;
}) => {
  const s = ChannelStates[state ?? "inreview"];
  return (
    <Chip tone={s?.tone ?? "neutral"} className={cn("shrink-0", className)}>
      {s?.label ?? state}
    </Chip>
  );
};

export default ChannelStateChip;
